import { graphql } from "gatsby"
import React, { useEffect, useMemo, useRef, useState } from "react"

import RequestDemoSection from "../components/request-demo-section"
import SEO from "../components/seo"
import CostControlIcon from "../images/case-study/Cost-Control-Icon.svg"
import LimitedPrecisionIcon from "../images/case-study/Limited-Precision-Icon.svg"
import NicheTargetingIcon from "../images/case-study/Niche-Targeting-Icon.svg"
import PMPHeader from "./index-components/pmp-header"
import PMPLayout from "./index-components/pmp-layout"
import { CaseStudyHero } from "./case-study/case-study-hero"
import {
  CaseStudyChallengeSection,
  CaseStudyExploreSection,
  CaseStudyResultsSection,
  CaseStudySolutionSection,
} from "./case-study/case-study-section"
import {
  buildChallengeCards,
  buildMetrics,
  parseSignals,
  removeInlineStyles,
  sanitizeHtmlTitle,
  sanitizeHtmlTitleForSEO,
} from "./case-study/case-study-helpers"
import { CaseStudySignalsGrid } from "./case-study/case-study-signals-grid"
import { getOptimalSignalColumns } from "./case-study/signal-constants"
import { useIsTablet } from "./case-study/useIsTablet"
import { useSignalSlots } from "./case-study/useSignalSlots"

const challengeIcons = [
  { src: NicheTargetingIcon, alt: "Niche Targeting" },
  { src: LimitedPrecisionIcon, alt: "Limited Precision" },
  { src: CostControlIcon, alt: "Cost Control" },
]

const LAT_LONG_REGEX = /latitude\s*(and|&|e)\s*longitude/i

const CaseStudySolutionBlock = ({ post }) => {
  const isTablet = useIsTablet()
  const solutionSectionRef = useRef(null)
  const [isSolutionSectionVisible, setIsSolutionSectionVisible] = useState(
    false
  )

  const signalsData = useMemo(() => parseSignals(post.signals), [post.signals])
  const { slotStates, allSlotsStopped, animationSettings } = useSignalSlots(
    signalsData,
    isSolutionSectionVisible
  )

  const signalsColumns = useMemo(
    () =>
      signalsData.length ? getOptimalSignalColumns(signalsData.length) : 4,
    [signalsData.length]
  )

  const formatSignalTitle = useMemo(
    () => title => {
      if (!title) return ""
      if (isTablet && LAT_LONG_REGEX.test(title)) {
        return "Lat. and Long."
      }
      return title
    },
    [isTablet]
  )

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof IntersectionObserver === "undefined"
    ) {
      return
    }

    const section = solutionSectionRef.current
    if (!section) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsSolutionSectionVisible(true)
          }
        })
      },
      {
        threshold: 0.1,
        rootMargin: "0px",
      }
    )

    observer.observe(section)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <CaseStudySolutionSection
      primaryText={post.sol_text_1}
      secondaryText={post.sol_text_2}
      removeInlineStyles={removeInlineStyles}
      ref={solutionSectionRef}
    >
      <CaseStudySignalsGrid
        signalsData={signalsData}
        slotStates={slotStates}
        allSlotsStopped={allSlotsStopped}
        animationSettings={animationSettings}
        signalsColumns={signalsColumns}
        formatSignalTitle={formatSignalTitle}
      />
    </CaseStudySolutionSection>
  )
}

const CaseStudyPage = ({ data }) => {
  const post = data.wordpressAcfCaseStudies.acf

  const challengeCards = useMemo(() => buildChallengeCards(post), [post])
  const metrics = useMemo(() => buildMetrics(post), [post])
  const heroBackgroundUrl =
    post.hero_detail?.localFile?.childImageSharp?.fluid?.src ?? ""
  const heroBackgroundUrlMobile =
    post.hero_mobile?.localFile?.childImageSharp?.fluid?.src ?? ""
  const heroTitle = sanitizeHtmlTitle(post.custom_post_title)
  const seoTitle = sanitizeHtmlTitleForSEO(post.custom_post_title)

  return (
    <PMPLayout showNavigation>
      <SEO
        title={`${seoTitle} | Case Study`}
        image={post.hero?.localFile.childImageSharp.fluid.src}
        pageType="article"
      />

      <div className="header-fallback" aria-hidden="true" />
      <PMPHeader />

      <CaseStudyHero
        titleHtml={heroTitle}
        backgroundUrl={heroBackgroundUrl}
        backgroundUrlMobile={heroBackgroundUrlMobile}
      />

      <CaseStudyChallengeSection
        primaryText={post.chal_text_1}
        secondaryText={post.chal_text_2}
        challengeCards={challengeCards}
        challengeIcons={challengeIcons}
        removeInlineStyles={removeInlineStyles}
      />

      <CaseStudySolutionBlock post={post} />

      <CaseStudyResultsSection
        primaryText={post.res_text_1}
        secondaryText={post.res_text_2}
        removeInlineStyles={removeInlineStyles}
        metrics={metrics}
      />

      <CaseStudyExploreSection slug={post.slug} />

      <div className="pt-6">
        <RequestDemoSection formPlacement={`Case Study: ${post.slug}`} />
      </div>
    </PMPLayout>
  )
}

export default CaseStudyPage

export const query = graphql`
  query($slug: String) {
    wordpressAcfCaseStudies(acf: { slug: { eq: $slug } }) {
      acf {
        slug
        custom_post_title
        chal_text_1
        chal_text_2
        chal_card_title_1
        chal_card_text_1
        chal_card_title_2
        chal_card_text_2
        chal_card_title_3
        chal_card_text_3
        sol_text_1
        sol_text_2
        signals
        res_text_1
        res_text_2
        metric_label_1
        metric_value_1
        metric_label_2
        metric_value_2
        metric_label_3
        metric_value_3
        metric_label_4
        metric_value_4

        hero {
          localFile {
            childImageSharp {
              fluid(maxHeight: 500, maxWidth: 1256, quality: 100) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
        hero_detail {
          localFile {
            childImageSharp {
              fluid(maxWidth: 2000, quality: 100, cropFocus: ATTENTION) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
        hero_mobile {
          localFile {
            childImageSharp {
              fluid(maxWidth: 800, quality: 100, cropFocus: ATTENTION) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
    }
  }
`
